import React, { useCallback, useContext, useEffect, useState } from "react";
/// React router dom
import { Link, useLocation } from "react-router-dom";
import { ThemeContext } from "../../../context/ThemeContext";
import {
  formatNavHeaderLocalDateTimeParts,
  parsePropertyLocalNowToDate,
} from "../../../services/common.utils";
import nbrApi from "../../../services/nbrApi";
import MonitorHeartbeatStatus from "../../pages/Monitor/MonitorHeartbeatStatus";

//images
import logo1 from './../../../images/logo/logo-full.png';

const HEARTBEAT_INTERVAL_MS = 15000;
/** When true, label includes seconds. */
const SHOW_SECONDS_IN_LOCAL_TIME = false;
/** Last successful heartbeat local_datetime — avoids flashing browser time on refresh. */
const NAV_HEARTBEAT_LOCAL_DATETIME_KEY = "navHeartbeatLocalDatetime";

function pickLocalDatetimeFromHeartbeat(payload) {
  if (!payload || typeof payload !== "object") return null;
  const tryVal = (v) =>
    v != null && String(v).trim() !== "" ? String(v).trim() : null;
  return (
    tryVal(payload.local_datetime) ??
    tryVal(payload.localDatetime) ??
    (payload.data && typeof payload.data === "object"
      ? tryVal(payload.data.local_datetime) ?? tryVal(payload.data.localDatetime)
      : null)
  );
}

function readCachedHeartbeatLocalDatetime(propertiesId) {
  if (propertiesId == null || String(propertiesId).trim() === "") return null;
  try {
    const cached = JSON.parse(localStorage.getItem(NAV_HEARTBEAT_LOCAL_DATETIME_KEY) || "null");
    if (
      cached &&
      String(cached.propertiesId) === String(propertiesId) &&
      typeof cached.localDatetime === "string" &&
      cached.localDatetime.trim() !== ""
    ) {
      return cached.localDatetime.trim();
    }
  } catch {
    /* ignore */
  }
  return null;
}

function writeCachedHeartbeatLocalDatetime(propertiesId, localDatetime) {
  if (propertiesId == null || localDatetime == null || String(localDatetime).trim() === "") return;
  try {
    localStorage.setItem(
      NAV_HEARTBEAT_LOCAL_DATETIME_KEY,
      JSON.stringify({
        propertiesId: String(propertiesId),
        localDatetime: String(localDatetime).trim(),
      })
    );
  } catch {
    /* ignore */
  }
}

function formatPartsFromLocalDatetimeRaw(raw) {
  const parsed = parsePropertyLocalNowToDate(raw);
  if (!parsed) return null;
  return formatNavHeaderLocalDateTimeParts(parsed, {
    includeSeconds: SHOW_SECONDS_IN_LOCAL_TIME,
  });
}

export function NavMenuToggle() {
  setTimeout(() => {
    let mainwrapper = document.querySelector("#main-wrapper");
    if (mainwrapper.classList.contains('menu-toggle')) {
      mainwrapper.classList.remove("menu-toggle");
    } else {
      mainwrapper.classList.add("menu-toggle");
    }
  }, 200);
}

const NavHader = () => {
  const location = useLocation();
  const monitorRoutePaths = ['/monitor', '/multi-property-monitor', '/room-status-history'];
  const isMonitorRoute = monitorRoutePaths.some(
    (path) => location.pathname === path || location.pathname.startsWith(`${path}/`)
  );
  const [toggle, setToggle] = useState(false);
  const [heartbeatLive, setHeartbeatLive] = useState(null);

  let selectedLoginData = null;
  try {
    selectedLoginData = JSON.parse(localStorage.getItem("selectedLoginData") || "null");
  } catch {
    selectedLoginData = null;
  }
  const propertiesId = selectedLoginData?.properties_id;

  // Prefer last heartbeat value for this property — never seed with browser local time.
  const [localTimeParts, setLocalTimeParts] = useState(() => {
    const cached = readCachedHeartbeatLocalDatetime(propertiesId);
    return formatPartsFromLocalDatetimeRaw(cached) || { dateLine: "", timeLine: "" };
  });

  const applyLocalDatetime = useCallback((raw, forPropertiesId) => {
    const parts = formatPartsFromLocalDatetimeRaw(raw);
    if (!parts) return;
    setLocalTimeParts(parts);
    writeCachedHeartbeatLocalDatetime(forPropertiesId, raw);
  }, []);

  useEffect(() => {
    const wrapper = document.getElementById('main-wrapper');
    wrapper?.classList.toggle('nav-header-has-heartbeat', isMonitorRoute);
    return () => wrapper?.classList.remove('nav-header-has-heartbeat');
  }, [isMonitorRoute]);

  // When property changes, show that property’s cached time (or blank) until heartbeat returns.
  useEffect(() => {
    const cached = readCachedHeartbeatLocalDatetime(propertiesId);
    const parts = formatPartsFromLocalDatetimeRaw(cached);
    setLocalTimeParts(parts || { dateLine: "", timeLine: "" });
  }, [propertiesId]);

  useEffect(() => {
    let cancelled = false;

    const fetchHeartbeat = async () => {
      if (propertiesId == null || String(propertiesId).trim() === "") {
        if (!cancelled) {
          setHeartbeatLive(false);
        }
        return;
      }

      try {
        const res = await nbrApi.get("/getheartbeat", {
          params: { properties_id: propertiesId },
          validateStatus: () => true,
        });
        if (cancelled) return;

        setHeartbeatLive(res.status === 200);

        if (res.status === 200) {
          const localDatetime = pickLocalDatetimeFromHeartbeat(res.data);
          if (localDatetime) {
            applyLocalDatetime(localDatetime, propertiesId);
          }
        }
      } catch {
        if (!cancelled) {
          setHeartbeatLive(false);
        }
      }
    };

    fetchHeartbeat();
    const intervalId = window.setInterval(fetchHeartbeat, HEARTBEAT_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [propertiesId, applyLocalDatetime]);

  const userDetailsAll = JSON.parse(localStorage?.getItem('userDetailsAll'))
  const userData = JSON.parse(localStorage.getItem('userDetails'))
  let selectedDataFilter = userData?.filter(e =>
    e?.properties_id === parseInt(selectedLoginData?.properties_id) &&
    e?.roles_id === Number(selectedLoginData?.role_id)
  )?.[0]
  let selectedData = userDetailsAll?.user_info ? userDetailsAll?.user_info : selectedDataFilter
  let propertyLogo = userDetailsAll?.session_info?.logo_url || null
  const { navigationHader, openMenuToggle, background } = useContext(
    ThemeContext
  );
	let role_data = userDetailsAll?.session_info?.role_name ?? null

  const isRoomopsRole =
		(role_data && String(role_data).toLowerCase().includes('roomops')) ||
		role_data?.toLowerCase() === 'roomops monitor' ||
		role_data?.toLowerCase() === 'roomops admin' ||
		role_data?.toLowerCase() === 'roomops manager'
	const roomopsLogoUrl = 'https://mcusercontent.com/2c3e5bcb748d60e5dc4040a94/images/65acae01-f8a5-195c-c91c-e92ac7d40a91.png'
	
  return (
    <div className="nav-header justify-content-lg-center d-flex">
      <Link
        to={isRoomopsRole ? "/multi-property-monitor" : "/dashboard"}
        className="nav-header-desktop-brand w-100 h-100 d-flex flex-column align-items-center justify-content-start text-decoration-none"
      >
        <div className="nav-header-logo-wrap d-flex align-items-center justify-content-center flex-wrap gap-2">
          {/* <img src={logo1} className="logo-abbr" alt="" /> */}
          {propertyLogo ? (
            <img
              src={propertyLogo}
              className="mx-0 logott"
              alt=""
              style={{
                maxHeight: 150,
                width: "auto",
                maxWidth: "min(92vw, 320px)",
                objectFit: "contain",
                borderRadius: '10px'
              }}
            />
          ) : (
            <span className="logo-t-title d-none d-lg-flex fs-3 text-white border rounded mt-1 px-3">{userDetailsAll?.session_info?.property_name}</span>
          )}
          {/* <img src={logo1} className="brand-title mx-0" alt="" />*/}
          <img
            src={logo1}
            className="logo-color m-0 m-sm-2"
            alt=""
            style={{ height: 56, width: "auto", objectFit: "contain" }}
          />
          {/* <img src={logo1} className="brand-title color-title" alt="" /> */}
        </div>
        <div className="nav-header-local-datetime text-white text-center px-1">
          <span className="nav-header-local-datetime__date d-block lh-sm">
            {localTimeParts.dateLine}
          </span>
          <span className="nav-header-local-datetime__time d-block lh-sm">
            {localTimeParts.timeLine}
          </span>
        </div>
        {isMonitorRoute && (
          <div
            className="nav-header-heartbeat"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <MonitorHeartbeatStatus isLive={heartbeatLive} />
          </div>
        )}
      </Link>

      <div
        className="nav-control"
        onClick={() => {
          setToggle(!toggle);
          //openMenuToggle();
          NavMenuToggle();
        }}
      >
        <div className={`hamburger ${toggle ? "is-active" : ""}`}>
          <span className="line"></span>
          <span className="line"></span>
          <span className="line"></span>
          <svg width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="22" y="11" width="4" height="4" rx="2" fill="#2A353A" />
            <rect x="11" width="4" height="4" rx="2" fill="#2A353A" />
            <rect x="22" width="4" height="4" rx="2" fill="#2A353A" />
            <rect x="11" y="11" width="4" height="4" rx="2" fill="#2A353A" />
            <rect x="11" y="22" width="4" height="4" rx="2" fill="#2A353A" />
            <rect width="4" height="4" rx="2" fill="#2A353A" />
            <rect y="11" width="4" height="4" rx="2" fill="#2A353A" />
            <rect x="22" y="22" width="4" height="4" rx="2" fill="#2A353A" />
            <rect y="22" width="4" height="4" rx="2" fill="#2A353A" />
          </svg>
        </div>
      </div>
    </div>
  );
};

export default NavHader;
